import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Globe, Facebook, Twitter, Instagram } from "lucide-react";
import { conference, navLinks, secretariat, organizer } from "../mock";

const Footer = () => {
  return (
    <footer className="bg-navy-900 text-slate-300">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-8">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="lg:col-span-4">
            <span className="bg-white rounded-xl px-3 py-2 inline-flex shadow-sm">
              <img src="/brand/drs_footer.png" alt="DRS 2026" className="h-16 w-auto object-contain" />
            </span>
            <p className="mt-5 text-sm leading-relaxed text-slate-400 max-w-sm">
              Professional excellence in diabetes & obesity care. Join the prestigious congress dedicated to diabesity
              research — {conference.datesShort}, {conference.venue}.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a href="https://www.instagram.com/drsmumbai/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-xl bg-white/5 hover:bg-brand flex items-center justify-center transition-colors" aria-label="Instagram">
                <Instagram className="w-4 h-4 text-white" />
              </a>
              <a href="https://www.facebook.com/DRSmumbai" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-xl bg-white/5 hover:bg-brand flex items-center justify-center transition-colors" aria-label="Facebook">
                <Facebook className="w-4 h-4 text-white" />
              </a>
              <a href="https://x.com/DRSmumbai" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-xl bg-white/5 hover:bg-brand flex items-center justify-center transition-colors" aria-label="X">
                <Twitter className="w-4 h-4 text-white" />
              </a>
            </div>
          </div>

          {/* Quick nav */}
          <div className="lg:col-span-2">
            <h4 className="text-brand font-semibold tracking-[0.18em] uppercase text-xs mb-5">Quick Navigation</h4>
            <ul className="space-y-3 text-sm">
              {navLinks
                .flatMap((l) => (l.children ? l.children : l))
                .filter((l) => l.to !== "/")
                .map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-slate-400 hover:text-white transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Secretariat */}
          <div className="lg:col-span-3">
            <h4 className="text-brand font-semibold tracking-[0.18em] uppercase text-xs mb-5">Conference Secretariat</h4>
            <span className="bg-white rounded-xl px-3 py-2 inline-flex shadow-sm mb-4">
              <img src="/brand/mdcf.png" alt="Mumbai Diabetes Care Foundation" className="h-12 w-auto object-contain" />
            </span>
            <p className="font-display font-bold text-white">{secretariat.org}</p>
            <p className="mt-3 text-sm text-slate-400 flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-brand" /> {secretariat.address}
            </p>
            {secretariat.phones.map((p) => (
              <p key={p} className="mt-3 text-sm text-slate-400 flex items-center gap-2">
                <Phone className="w-4 h-4 shrink-0 text-brand" /> <a href={`tel:${p}`} className="hover:text-white transition-colors">{p}</a>
              </p>
            ))}
            <p className="mt-3 text-sm text-slate-400 flex items-center gap-2">
              <Mail className="w-4 h-4 shrink-0 text-brand" /> <a href={`mailto:${secretariat.email}`} className="hover:text-white transition-colors">{secretariat.email}</a>
            </p>
          </div>

          {/* Organizer */}
          <div className="lg:col-span-3">
            <h4 className="text-brand font-semibold tracking-[0.18em] uppercase text-xs mb-5">Conference Organizer</h4>
            <span className="bg-white rounded-xl p-2.5 inline-flex shadow-sm mb-4">
              <img src="/brand/rx.png" alt="Rx Events" className="h-14 w-auto object-contain" />
            </span>
            <p className="font-display font-bold text-white">{organizer.org}</p>
            <p className="mt-3 text-sm text-slate-400 flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-brand" /> {organizer.address}
            </p>
            {organizer.phones.map((p) => (
              <p key={p} className="mt-3 text-sm text-slate-400 flex items-center gap-2">
                <Phone className="w-4 h-4 shrink-0 text-brand" /> <a href={`tel:${p}`} className="hover:text-white transition-colors">{p}</a>
              </p>
            ))}
            <p className="mt-3 text-sm text-slate-400 flex items-center gap-2">
              <Mail className="w-4 h-4 shrink-0 text-brand" /> <a href={`mailto:${organizer.email}`} className="hover:text-white transition-colors">{organizer.email}</a>
            </p>
            <p className="mt-3 text-sm text-slate-400 flex items-center gap-2">
              <Globe className="w-4 h-4 shrink-0 text-brand" /> <a href={`https://${organizer.website}`} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">{organizer.website}</a>
            </p>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-500">
            © {conference.year} DRS — Diabetes Research & Solutions. All rights reserved.
          </p>
          <div className="flex items-center gap-5 text-xs text-slate-500">
            <span>Organised by <span className="text-brand font-semibold">{conference.organisedBy}</span></span>
            <span className="text-slate-600">|</span>
            <span>Designed & Developed by <a href="https://www.rxevents.co.in" target="_blank" rel="noopener noreferrer" className="text-brand font-semibold hover:text-white transition-colors">Rx Events</a></span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
